import React, { useState } from "react";
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';
import { USDZExporter } from 'three/examples/jsm/exporters/USDZExporter';
import { 
  ChevronLeft, 
  Expand, 
  Palette,
  CircleEllipsis,
  Grid2X2,
  MessageCircle,
  Box,
  FileText
} from 'lucide-react';
import DayNightSwitcher from "./DayNightSwitcher";
import ToggleSwitch from "./ToggleSwitch";
import { exportConfigurationPDF } from '../utils/pdfExport';
import PDFPreview from './PDFPreview';


function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isAndroid() {
  return /Android/.test(navigator.userAgent);
}

function ExportButton({ scene }) {
  
  const openSceneViewer = () => {
    // const modelUrl = "https://cdn.jsdelivr.net/gh/KhronosGroup/glTF-Sample-Models/2.0/Duck/glTF-Binary/Duck.glb"; // Google-hosted 3D model
    const modelUrl = "https://taapp.bitquid.com/TAAPP_Pergola_3.glb"; // Google-hosted 3D model
    const sceneViewerUrl = `intent://arvr.google.com/scene-viewer/1.0?file=${encodeURIComponent(modelUrl)}&mode=ar_preferred#Intent;scheme=https;package=com.google.ar.core;end;`;
    // console.log(sceneViewerUrl);
    
    window.location.href = sceneViewerUrl;
};

    const exportModel = () => {
      if (isIOS()) {
        const exporter = new USDZExporter();
        exporter.parse(scene, (usdz) => {
          const blob = new Blob([usdz], { type: 'model/vnd.usdz+zip' });
          const url = URL.createObjectURL(blob);
          window.location.href = url;
        });
      } else if (isAndroid()) {
        // **Android: Export as GLB & Open Google Scene Viewer**
        const exporter = new GLTFExporter();
        exporter.parse(
          scene,
          (gltf) => {
            // const blob = new Blob([JSON.stringify(gltf)], { type: 'model/gltf+json' });
            const blob = new Blob([gltf], { type: "model/gltf-binary" });
            const url = URL.createObjectURL(blob);
  
            // Open in Google Scene Viewer
            // const sceneViewerUrl = `https://arvr.google.com/scene-viewer/1.0?file=${url}&mode=ar_preferred`;
            const sceneViewerUrl = `intent://arvr.google.com/scene-viewer/1.0?file=${encodeURIComponent(url)}&mode=ar_preferred#Intent;scheme=https;package=com.google.ar.core;end;`;
            window.location.href = sceneViewerUrl;
          },
          { binary: true }
        );
      } else {
        const exporter = new GLTFExporter();
        exporter.parse(
          scene,
          (gltf) => {
            const blob = new Blob([JSON.stringify(gltf)], { type: 'model/gltf+json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'TAAPP_Pergola.glb';
            link.click();
          },
          { binary: true }
        );
      }
    };
  
    return (
      <div className="flex flex-col space-y-3">
        {isIOS()&&
          <button
            onClick={exportModel}
            className="flex items-center mx-auto justify-center w-full bg-blue-500 text-white py-3 rounded-lg shadow-md transition hover:bg-blue-600"
          >
            <Box size={18} className="mr-2" />
            Augmented Reality
          </button>
          }

        {/* Test Button to Simulate AR Core on iOS */}
        {/* <button
            onClick={openSceneViewer}
            className="flex items-center mx-auto justify-center w-full bg-green-500 text-white py-3 rounded-lg shadow-md transition hover:bg-green-600"
        >
            <Box size={18} className="mr-2"/>
            Test Google
          
        </button> */}
        
      </div>
    );
}

const Sidebar = ({
  config,
  setConfig,
  tempConfig,
  darkMode,
  setDarkMode,
  infosVisible,
  setInfosVisible,
  sidebarOpen,
  setSidebarOpen,
  toggleGlassWall,
  handleConfigChange,
  handleTempConfigChange,
  handleColorChange,
  area,
  sceneRef,
  showRequestForm,
  setShowRequestForm,
}) => {
  const [activeSection, setActiveSection] = useState('dimensions');
  const [pdfExportMessage, setPDFExportMessage] = useState('');
  const [showPDFPreview, setShowPDFPreview] = useState(false);


  // **Export PDF**
  const handlePDFExport = async () => {
    setPDFExportMessage('');
    
    try {
      // Get the Three.js canvas element
      const canvas = document.querySelector('canvas');
      
      if (!canvas) {
        throw new Error('Canvas non trovato');
      }
      
      const result = await exportConfigurationPDF(config, canvas);
      
      if (result.success) {
        setPDFExportMessage('✅ PDF scaricato con successo!');
      } else {
        setPDFExportMessage('❌ ' + result.message);
      }
    } catch (error) {
      console.error('PDF Export Error:', error);
      setPDFExportMessage('❌ Errore durante l\'esportazione');
    } finally {
      // Clear message after 3 seconds
      setTimeout(() => setPDFExportMessage(''), 3000);
    }
  };


  const renderDimensionsSection = () => (
    <div className="space-y-4">
      {['width', 'length', 'height', 'modules'].map((dimension) => (
        <div key={dimension} className="flex justify-between items-center">
          <label className="text-sm capitalize">
            {dimension === 'width' ? 'Larghezza' : 
             dimension === 'length' ? 'Lunghezza' : 
             dimension === 'height' ? 'Altezza' : 
             'Moduli'} (m)
          </label>
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => handleConfigChange({
                target: {
                  name: dimension, 
                  value: `${config[dimension] - (dimension === 'modules' ? 1 : 0.1)}`
                }
              })}
              className="bg-gray-100 p-1 rounded-full"
            >
              −
            </button>
            <input
              type="number"
              inputMode="decimal"
              name={dimension}
              value={tempConfig[dimension] || ""}
              onChange={handleTempConfigChange}
              className="w-16 text-center border border-gray-300 p-1 rounded"
              onBlur={(e) => {
                
                let updatedValue = parseFloat(e.target.value);
                
                if (!updatedValue || isNaN(updatedValue)) {
                  updatedValue = config[dimension]; // Reset if invalid input
                }
                
                // Apply constraints based on dimension type
                if (dimension === 'width' || dimension === 'length' || dimension === 'height') {
                  // updatedValue = updatedValue?updatedValue.replace(/^0+/, ''):updatedValue;
                  updatedValue = Math.max(0, Number(updatedValue));
                  
                  if (dimension === 'width') {
                      updatedValue = Math.max(2.4, updatedValue); // Min width
                      const minModules = Math.max(1, Math.ceil(updatedValue / 4.5)); // At least 1 module per 4.5m                        
                      setConfig((prev) => ({
                          ...prev,
                          width: updatedValue,
                          modules: Math.max(minModules, prev.modules), // Keep manual modules if higher
                      }));
                  } else if (dimension === 'length') {
                    updatedValue = Math.min(4.5, Math.max(2.4, updatedValue)); // Min 2.4m, Max 4.5m
                    setConfig((prev) => ({ ...prev, length: updatedValue }));
                  } else if (dimension === 'height') {
                      updatedValue = Math.min(3.5, Math.max(2.4, updatedValue));
                      setConfig((prev) => ({ ...prev, height: updatedValue }));
                  }
              }else if (dimension === 'modules') {
                  updatedValue = Math.max(1, Math.ceil(updatedValue)); // Ensure whole numbers for modules
                }

                // Set the final validated value
                handleConfigChange({
                  target: { 
                    name: dimension, 
                    value: updatedValue 
                  }
                });
              }}
            />
            <button 
              onClick={() => handleConfigChange({
                target: {
                  name: dimension, 
                  value: `${config[dimension] + (dimension === 'modules' ? 1 : 0.1)}`
                }
              })}
              className="bg-gray-100 p-1 rounded-full"
            >
              +
            </button>
          </div>
        </div>
      ))}
      <p className="text-center text-sm text-gray-600">
        Area: <span className="font-semibold">{isNaN(area)? '-': area } m²</span>
      </p>
    </div>
  );

  const renderOptionsSection = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <img src={`${process.env.PUBLIC_URL || ''}/images/icon_wall_mount.png`} alt="wall mount icon" className="w-10 h-10" />
        <label className="flex items-center space-x-2">
          <span>Montaggio a Parete</span>
        </label>
        <ToggleSwitch
          name="wallMount"
          checked={config.wallMount}
          onChange={() =>
            setConfig((prevConfig) => ({
              ...prevConfig,
              wallMount: !prevConfig.wallMount,
              glassWalls: { ...prevConfig.glassWalls, retro: false },
            }))
          }
        />
        {/* <Move className="text-gray-500" size={20} /> */}
      </div>

      <div className="flex items-center justify-between">
          <img src={`${process.env.PUBLIC_URL || ''}/images/icon_v-h_strips.png`} alt="open strips icon" className="w-10 h-10" />
        <label className="flex items-center space-x-2">
          <span>Apertura {config.stripRotationHorizontal?'Verticale':'Orizzontale'}</span>
        </label>
        <ToggleSwitch
          name="stripRotationHorizontal"
          checked={config.stripRotationHorizontal}
          onChange={() =>
            setConfig((prev) => ({ 
              ...prev, 
              stripRotationHorizontal: !prev.stripRotationHorizontal 
            }))
          }
        />
      </div>

      <div className="flex items-center justify-between">
          <img src={`${process.env.PUBLIC_URL || ''}/images/icon_led_perimeter.png`} alt="led icon" className="w-10 h-10" />
        <label className="flex items-center space-x-2">
          <span>Led Perimetrale</span>
        </label>
        {/* <Lightbulb className="text-gray-500" size={20} /> */}
        <ToggleSwitch
          name="perimeterLed"
          checked={config.perimeterLed}
          onChange={() =>
            setConfig((prev) => ({ 
              ...prev, 
              perimeterLed: !prev.perimeterLed 
            }))
          }
        />
      </div>

      <div className="flex items-center justify-between">
          <img src={`${process.env.PUBLIC_URL || ''}/images/icon_led_roof.png`} alt="led icon" className="w-10 h-10" />
        <label className="flex items-center space-x-2">
          <span>Led Tetto</span>
        </label>
        {/* <Lightbulb className="text-gray-500" size={20} /> */}
        <ToggleSwitch
          name="roofLed"
          checked={config.roofLed}
          onChange={() =>
            setConfig((prev) => ({ 
              ...prev, 
              roofLed: !prev.roofLed 
            }))
          }
        />
      </div>

      {/* Tetto motorizzato */}
      <div className="flex items-center justify-between">
          <img src={`${process.env.PUBLIC_URL || ''}/images/icon_motorized_open.png`} alt="motorized icon" className="w-10 h-10" />
        <label className="flex items-center space-x-2">
          <span>Tetto Motorizzato</span>
        </label>
        {/* <Slice className="text-gray-500" size={20} /> */}
        <ToggleSwitch
          name="motorized"
          checked={config.motorized}
          // onChange={()=>handleConfigChange({
          //   target: {
          //     name: "motorized",
          //     value: !config.motorized,
          //   },
          // })}
          onChange={() =>
            setConfig((prev) => ({ 
              ...prev, 
              motorized: !prev.motorized 
            }))
          }
        />
      
      </div>
    </div>
  );

  const renderColorSection = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-6 gap-2">
        {[
          'white', 
          'black', 
          '#804A00', 
          'red', 
          'forestgreen', 
          '#EDC9AF'
        ].map((color) => (
          <button
            key={color}
            className={`w-10 h-10 rounded-full shadow-md ${
              config.color === color ? 'border-2 border-gray-300' : ''
            }`}
            style={{ backgroundColor: color }}
            onClick={() => handleColorChange(color)}
          />
        ))}
      </div>
    </div>
  );

  const renderGlassSection = () => (
    <div className="space-y-4">

<div className="flex items-center justify-between">
        <img src={`${process.env.PUBLIC_URL || ''}/images/icon_glasses.png`} alt="glass icon" className="w-10 h-10" />
      <label className="flex items-center space-x-2">
        <span>Abilita Vetri</span>
      </label>
      <ToggleSwitch
        name="glass"
        checked={config.glass}
        onChange={() =>
          setConfig((prev) => ({ 
            ...prev, 
            glass: !prev.glass 
          }))
        }
      />
  </div>

      {config.glass && (
        <div className="space-y-2 ml-4">
          {["fronte", "retro", "sinistra", "destra"].map((wall) => (
            <div  key={wall} className="flex items-center justify-between">
              <label className="flex items-center space-x-2">
                <span>{wall.charAt(0).toUpperCase() + wall.slice(1)}</span>
              </label>
              <ToggleSwitch
                name="glass"
                checked={config.glassWalls?.[wall] ?? false}
                onChange={() => toggleGlassWall(wall)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    // <div className={`fixed inset-y-0 left-0 w-80 bg-white/90 backdrop-blur-md shadow-xl transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-1/4'}`}>
    <div
        className={`absolute top-0 left-0 h-full w-80 bg-white shadow-lg p-4 z-20 transition-transform duration-300 overflow-y-auto ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="p-4 flex justify-between items-center border-b">
          <div className="flex flex-col items-center w-full">
            <div className="flex w-full items-start space-x-2 mb-5">
              <a 
                href="/"
                className="text-blue-600 hover:underline text-sm font-medium"
                title="Torna alla Home"
              >
                ← Home
              </a>
            </div>
            <h2 className="text-xl font-semibold">Configurazione</h2>
          </div>
          {/* <h2 className="text-xl font-semibold">Configurazione</h2> */}
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)} 
            className="text-gray-600 hover:text-black"
          >
            <ChevronLeft size={24} />
          </button>
        </div>


        {/* Toggles */}
        <div className="p-4 border-b flex justify-between items-center">
          <div className="flex items-center space-x-2">
            {/* {darkMode ? <Moon size={20} /> : <Sun size={20} />} */}
            <span>{`${darkMode?'Modalità Notte':'Modalità Giorno'}`}</span>
          </div>
          <div className="flex justify-end">
            <DayNightSwitcher darkMode={darkMode} setDarkMode={setDarkMode} className="" />
          </div>
          {/* <input
            type="checkbox"
            checked={darkMode}
            onChange={() => setDarkMode(!darkMode)}
            className="form-switch"
          /> */}
        </div>

        {/* Navigation */}
        <div className="flex border-b">
          {[
            { id: 'dimensions', label: 'Dimensioni', icon: Expand },
            { id: 'options', label: 'Opzioni', icon: CircleEllipsis },
            { id: 'color', label: 'Colore', icon: Palette },
            { id: 'glass', label: 'Vetri', icon: Grid2X2 }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveSection(id)}
              className={`flex-1 p-3 text-center ${
                activeSection === id 
                  ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-500' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <div className="flex flex-col items-center space-y-1">
                <Icon size={20} />
                <span className="text-xs">{label}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="p-4 flex-grow overflow-y-auto">
          {activeSection === 'dimensions' && renderDimensionsSection()}
          {activeSection === 'options' && renderOptionsSection()}
          {activeSection === 'color' && renderColorSection()}
          {activeSection === 'glass' && renderGlassSection()}
        </div>

        {/* Export Buttons */}
        <div className="p-4 border-t space-y-3">
          {/* Request Quote Button */}
          <button
            onClick={() => setShowRequestForm(true)}
            className="flex items-center justify-center w-full bg-green-600 text-white py-3 rounded-lg shadow-md transition hover:bg-green-700"
          >
            <MessageCircle size={18} className="mr-2" />
            Richiedi Preventivo
          </button>

          {/* PDF Export Button */}
          <button
            onClick={() => setShowPDFPreview(true)}
            className="flex items-center justify-center w-full bg-red-500 text-white py-3 rounded-lg shadow-md transition hover:bg-red-600"
          >
            <FileText size={18} className="mr-2" />
            Anteprima e Scarica PDF
          </button>

          {/* PDF Export Message */}
          {pdfExportMessage && (
            <div className="text-center text-sm font-medium">
              {pdfExportMessage}
            </div>
          )}

          {/* 3D Model Export Button */}
          {sceneRef.current && <ExportButton scene={sceneRef.current} />}
        </div>


      </div>

      {/* PDF Preview Modal */}
      <PDFPreview
        isOpen={showPDFPreview}
        onClose={() => setShowPDFPreview(false)}
        config={config}
        onDownloadPDF={handlePDFExport}
      />
    </div>
  );
};

export default Sidebar;